import { debounce } from 'lodash-es'
import { useEffect, useRef, useCallback, useMemo } from 'react'

import { safeGetBoundingClientRect, safeElementAccess, safeWindowAccess } from 'shared/utils'


export const useOnScroll = (ref, onScroll, options = {}) => {
  const {
    vertical = true,
    horizontal = true,
    debounceMs = 50
  } = options

  const lastScrollPosition = useRef({ top: 0, left: 0 })
  const lastMethod = useRef('scrollbar')
  const pendingMethod = useRef(null)
  const methodTimeout = useRef(null)

  const createScrollEvent = (element, method) => ({
    isScrolling: true,
    scrollTop: element.scrollTop,
    scrollLeft: element.scrollLeft,
    scrollWidth: element.scrollWidth,
    scrollHeight: element.scrollHeight,
    clientWidth: element.clientWidth,
    clientHeight: element.clientHeight,
    method,
    timestamp: Date.now(),
  })

  const debouncedOnScroll = useMemo(
    () => debounce(onScroll, debounceMs),
    [onScroll, debounceMs]
  )

  const handleScroll = useCallback(() => {
    const element = ref.current
    if (!element) return

    const { scrollTop, scrollLeft } = element
    const { top: lastTop, left: lastLeft } = lastScrollPosition.current

    if ((vertical && scrollTop !== lastTop) || (horizontal && scrollLeft !== lastLeft)) {
      const method = pendingMethod.current || lastMethod.current
      const scrollEvent = createScrollEvent(element, method)
      debouncedOnScroll(scrollEvent)

      lastScrollPosition.current = { top: scrollTop, left: scrollLeft }
      lastMethod.current = method
      pendingMethod.current = null
    }
  }, [ref, debouncedOnScroll, vertical, horizontal])

  useEffect(() => {
    const element = ref.current
    if (!element) return null

    const setPendingMethod = (method) => {
      pendingMethod.current = method
      clearTimeout(methodTimeout.current)
      methodTimeout.current = setTimeout(() => {
        pendingMethod.current = null
      }, 100)
    }

    const handlers = {
      wheel: () => setPendingMethod('wheel'),
      touchstart: () => setPendingMethod('touch'),
      gesturestart: () => setPendingMethod('gesture'),
      mousedown: (event) => {
        const { clientX, clientY } = event

        // Safe DOM access using utility function
        const rect = safeGetBoundingClientRect(element)
        if (!rect) return

        const { right, bottom } = rect
        const isVerticalScrollbar = clientX >= right - 16
        const isHorizontalScrollbar = clientY >= bottom - 16

        if ((vertical && isVerticalScrollbar) || (horizontal && isHorizontalScrollbar)) {
          setPendingMethod('scrollbar')
        }
      },
      mousemove: (event) => {
        // Safe DOM property access using utility functions
        const clientWidth = safeElementAccess(element, 'clientWidth', 0)
        const clientHeight = safeElementAccess(element, 'clientHeight', 0)

        if (clientWidth && clientHeight
            && (event.offsetX > clientWidth || event.offsetY > clientHeight)) {
          setPendingMethod('scrollbar')
        }
      }
    }

    Object.entries(handlers).forEach(([event, handler]) => {
      element.addEventListener(event, handler, { passive: true })
    })

    const documentHandlers = {
      keydown: (event) => {
        const scrollKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End', 'Space']
        if (scrollKeys.includes(event.code)) {
          setPendingMethod('keyboard')
        } else if (event.code === 'Tab') {
          setPendingMethod('tab')
        }
      },
      selectionchange: () => {
        // Safe window and DOM access using utility functions
        const getSelection = safeWindowAccess('getSelection')
        if (!getSelection) return

        try {
          const selection = getSelection()
          if (selection && !selection.isCollapsed) {
            let node = selection.anchorNode
            while (node && node !== element) {
              node = node.parentNode
            }
            if (node === element) {
              setPendingMethod('text-selection')
            }
          }
        } catch (error) {
          // Ignore DOM access errors on iOS Chrome
          console.warn('DOM access error in selectionchange handler:', error)
        }
      }
    }

    Object.entries(documentHandlers).forEach(([event, handler]) => {
      document.addEventListener(event, handler, { passive: true })
    })

    element.addEventListener('scroll', handleScroll, { passive: true })

    const originalScrollTo = element.scrollTo
    const originalScrollBy = element.scrollBy

    if (originalScrollTo) {
      element.scrollTo = (...args) => {
        setPendingMethod('programmatic')
        originalScrollTo.apply(element, args)
      }
    }

    if (originalScrollBy) {
      element.scrollBy = (...args) => {
        setPendingMethod('programmatic')
        originalScrollBy.apply(element, args)
      }
    }

    return () => {
      Object.entries(handlers).forEach(([event, handler]) => {
        element.removeEventListener(event, handler)
      })
      Object.entries(documentHandlers).forEach(([event, handler]) => {
        document.removeEventListener(event, handler)
      })
      element.removeEventListener('scroll', handleScroll)
      element.scrollTo = originalScrollTo
      element.scrollBy = originalScrollBy
      clearTimeout(methodTimeout.current)
    }
  }, [ref, handleScroll, vertical, horizontal])
}
